import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Code, Database, Cloud, Wrench, ChevronDown } from 'lucide-react';
import { skillCategories } from '../../data/mock';

const iconMap = { Bot, Code, Database, Cloud, Wrench };

const SkillsSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const totalSkills = skillCategories.reduce((n, c) => n + c.skills.length, 0);

  return (
    <section id="skills" className="py-24 relative z-10">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="section-label block mb-4"
        >
          {`// ${totalSkills} TOOLS, ${skillCategories.length} STACKS`}
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-12"
        >
          Tech Arsenal
        </motion.h2>

        <div className="space-y-4">
          {skillCategories.map((category, index) => {
            const Icon = iconMap[category.icon];
            const isExpanded = expandedIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <button
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  className="w-full glass-card rounded-xl p-5 flex items-center justify-between transition-all duration-300"
                  style={{ borderColor: isExpanded ? `${category.color}40` : undefined }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: `${category.color}15` }}
                    >
                      <Icon size={20} style={{ color: category.color }} />
                    </div>
                    <span className="font-display font-semibold text-lg text-[#F0F4FF]">
                      {category.title}
                    </span>
                    <span className="font-mono text-xs text-[#8892A4] hidden sm:inline">
                      ({category.skills.length})
                    </span>
                    {!isExpanded && (
                      <span className="hidden lg:flex items-center gap-1.5 ml-2">
                        {category.skills.slice(0, 3).map((skill, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded-full text-xs font-mono"
                            style={{ color: `${category.color}99`, border: `1px solid ${category.color}20` }}
                          >
                            {skill}
                          </span>
                        ))}
                        <span className="font-mono text-xs text-[#4A5668]">
                          +{category.skills.length - 3} more
                        </span>
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    size={20}
                    className="text-[#8892A4] transition-transform duration-300"
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 py-4 flex flex-wrap gap-2">
                        {category.skills.map((skill, i) => (
                          <motion.span
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="px-3 py-1.5 rounded-full text-sm font-mono glass-card cursor-default transition-all duration-200"
                            style={{
                              borderColor: `${category.color}30`,
                              color: category.color,
                            }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
